import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money } from "../../lib/api";

const STATUSES = ["awaiting_payment", "paid", "in_production", "ready", "shipped", "complete", "cancelled", "refunded"];

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => api("/admin/orders").then(setOrders).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const save = async (o, patch) => {
    try {
      await api(`/admin/orders/${o.id}`, { method: "PATCH", body: patch });
      toast("Order updated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!orders) return <p className="text-muted font-bold">Fetching orders…</p>;

  return (
    <div data-testid="admin-orders">
      <h1 className="font-display font-semibold text-2xl">Orders</h1>
      {orders.length === 0 ? <p className="text-muted font-semibold mt-6">No orders yet — they'll appear here the moment one lands.</p> : (
        <div className="mt-6 space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-white rounded-3xl border border-candy-soft/70 p-4" data-testid={`admin-order-${o.order_number}`}>
              <button className="w-full flex flex-wrap items-center gap-3 text-left" data-testid={`admin-order-toggle-${o.order_number}`} onClick={() => setOpen(open === o.id ? null : o.id)}>
                <span className="font-display font-semibold">{o.order_number}</span>
                <span className="text-sm text-muted font-bold">{o.name} · {o.email}</span>
                <span className="ml-auto font-bold">{money(o.total_cents)}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${o.payment_status === "paid" ? "bg-mint-soft text-emerald-700" : "bg-butter-soft text-amber-700"}`}>{o.status.replace(/_/g, " ")}</span>
              </button>
              {open === o.id && (
                <div className="mt-4 border-t border-candy-soft pt-4 grid md:grid-cols-2 gap-4 text-sm" data-testid={`admin-order-detail-${o.order_number}`}>
                  <div>
                    <p className="font-bold mb-1">Items</p>
                    <ul className="space-y-1 text-ink/70 font-semibold">
                      {o.items.map((it, i) => <li key={i}>{it.title}{it.variant_label ? ` — ${it.variant_label}` : ""} × {it.qty} — {money(it.price_cents * it.qty)}</li>)}
                    </ul>
                    {o.gift_note && <p className="mt-2 bg-candy-mist rounded-xl p-2 font-semibold">Gift note: {o.gift_note}</p>}
                    {o.fulfillment_issues?.length > 0 && <p className="mt-2 text-red-500 font-bold">⚠ {o.fulfillment_issues.join("; ")}</p>}
                  </div>
                  <div className="space-y-3">
                    <p className="text-ink/70 font-semibold">Ship to: {o.address?.line1}, {o.address?.city} {o.address?.state} {o.address?.zip}</p>
                    <label className="block">
                      <span className="font-bold">Fulfillment status</span>
                      <select data-testid={`admin-order-status-${o.order_number}`} value={o.status} onChange={(e) => save(o, { status: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft font-semibold">
                        {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="font-bold">Tracking number</span>
                      <input data-testid={`admin-order-tracking-${o.order_number}`} defaultValue={o.tracking_number} placeholder="Manual carrier entry" onBlur={(e) => e.target.value !== o.tracking_number && save(o, { tracking_number: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft font-semibold" />
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
